import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, AlertTriangle, FileText, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/apiBase';


const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
};


function ResumeCard({ r }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const text = r.resume_text || '';
  const preview = text.length > 280 ? text.slice(0, 280) + '…' : text;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Resume text copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }, [text]);

  return (
    <div
      className="bg-white border border-[#e5e7eb] p-4"
      data-testid={`admin-resume-card-${r.resume_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={14} className="text-[#002fa7] flex-shrink-0" />
            <span
              className="font-mono text-xs text-[#6b7280] truncate"
              title={r.resume_id}
              data-testid={`admin-resume-id-${r.resume_id}`}
            >
              {r.resume_id}
            </span>
          </div>
          <p
            className="font-satoshi text-sm font-semibold text-[#0f1115]"
            data-testid={`admin-resume-target-${r.resume_id}`}
          >
            {r.target_job || <span className="text-[#9ca3af] font-normal">No target job set</span>}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#e5e7eb] hover:bg-[#f4f5f7] transition-colors"
          data-testid={`admin-resume-copy-${r.resume_id}`}
          title="Copy full resume text to clipboard"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>

      <pre
        className={`whitespace-pre-wrap break-words text-xs text-[#4b5563] bg-[#f4f5f7] p-3 border border-[#e5e7eb] font-mono leading-relaxed ${
          expanded ? '' : 'max-h-40 overflow-hidden'
        }`}
        data-testid={`admin-resume-text-${r.resume_id}`}
      >
        {expanded ? text : preview}
      </pre>

      {text.length > 280 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-[#002fa7] hover:underline"
          data-testid={`admin-resume-toggle-${r.resume_id}`}
        >
          {expanded ? 'Show less' : `Show full text (${text.length.toLocaleString()} chars)`}
        </button>
      )}

      <div className="mt-3 pt-3 border-t border-[#e5e7eb] flex items-center justify-between text-xs text-[#6b7280]">
        <span><span className="text-[#9ca3af]">Created:</span> {fmtDate(r.created_at)}</span>
        <span><span className="text-[#9ca3af]">Updated:</span> {fmtDate(r.updated_at)}</span>
      </div>
    </div>
  );
}


export default function UserResumesModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    setLoading(true);
    setErr('');
    axios.get(`${API}/admin/users/${user.user_id}/resumes`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.response?.data?.detail || 'Failed to load resumes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.user_id]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-resumes-modal-backdrop"
    >
      <div
        className="bg-white border border-[#e5e7eb] w-full max-w-3xl my-12 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-resumes-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#e5e7eb] bg-[#f4f5f7]">
          <div className="min-w-0">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115]">
              Resumes for <span className="text-[#002fa7]">{user.email}</span>
            </h2>
            <p className="text-xs text-[#6b7280] mt-1 font-mono">{user.user_id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-1.5 text-[#6b7280] hover:text-[#0f1115] hover:bg-white transition-colors"
            data-testid="admin-resumes-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#6b7280]" data-testid="admin-resumes-loading">
              <Loader2 size={18} className="animate-spin mr-2" /> Loading resumes…
            </div>
          ) : err ? (
            <div className="p-4 bg-red-50 border border-red-200 text-red-800 flex items-center gap-2" data-testid="admin-resumes-error">
              <AlertTriangle size={16} /> {err}
            </div>
          ) : !data || data.count === 0 ? (
            <div className="py-12 text-center" data-testid="admin-resumes-empty">
              <FileText size={28} className="mx-auto text-[#9ca3af] mb-3" />
              <p className="text-sm text-[#6b7280]">This user hasn't created any resumes yet.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-[#6b7280] mb-4" data-testid="admin-resumes-count">
                <strong className="text-[#0f1115]">{data.count}</strong> resume{data.count !== 1 ? 's' : ''} on record — newest first
              </p>
              <div className="space-y-4">
                {data.resumes.map((r) => (
                  <ResumeCard key={r.resume_id} r={r} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
